// src/components/StorageStats.js
import React, { useState, useEffect } from 'react';
import threadService from '../services/threadService';

/**
 * Component to display database storage statistics
 */
const StorageStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const storageStats = await threadService.getStorageStats();
        setStats(storageStats);
        setError(null);
      } catch (err) {
        console.error("Error fetching storage stats:", err);
        setError("Failed to load storage statistics");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    
    // Refresh stats every 2 minutes
    const interval = setInterval(fetchStats, 120000);
    
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="storage-stats loading">
        <p>Loading storage statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="storage-stats error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="storage-stats">
      <h3>Storage Statistics</h3>
      <div className="stats-grid">
        <div className="stat-item">
          <div className="stat-label">Conversations</div>
          <div className="stat-value">{stats.threadCount}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Messages</div>
          <div className="stat-value">{stats.messageCount}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Database Size</div>
          <div className="stat-value">{stats.estimatedSizeMB} MB</div>
        </div>
      </div>
      
      {/* Export functionality */}
      <div className="storage-actions">
        <button 
          onClick={async () => {
            try {
              const allThreadsJson = await threadService.exportAllThreads();
              const blob = new Blob([allThreadsJson], {type: 'application/json'});
              const url = URL.createObjectURL(blob);
              
              const a = document.createElement('a');
              a.href = url;
              a.download = `blue-comet-backup-${new Date().toISOString().split('T')[0]}.json`;
              document.body.appendChild(a);
              a.click();
              
              // Clean up
              setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }, 100);
            } catch (err) {
              console.error("Error exporting threads:", err);
              setError("Failed to export threads");
            }
          }}
        >
          Export All Conversations
        </button>
      </div>
      
      <style jsx>{`
        .storage-stats {
          padding: 16px;
          background-color: #f8f9fc;
          border-radius: 8px;
          margin-top: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .storage-stats h3 {
          margin-top: 0;
          margin-bottom: 16px;
          font-size: 16px;
          color: #4a5568;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          margin-bottom: 16px;
        }
        
        .stat-item {
          background-color: white;
          padding: 12px;
          border-radius: 6px;
          text-align: center;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }
        
        .stat-label {
          font-size: 12px;
          color: #718096;
          margin-bottom: 4px;
        }
        
        .stat-value {
          font-size: 18px;
          font-weight: 600;
          color: #2d3748;
        }
        
        .storage-actions {
          display: flex;
          justify-content: center;
          margin-top: 12px;
        }
        
        .storage-actions button {
          background-color: #4299e1;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .storage-actions button:hover {
          background-color: #3182ce;
        }
        
        .storage-stats.loading,
        .storage-stats.error {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 120px;
        }
        
        .storage-stats.error {
          color: #e53e3e;
        }
      `}</style>
    </div>
  );
};

export default StorageStats;